import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { fetchCommunity, fetchAnalysis, fetchCommunityConversation, toggleLike } from '../../utils/api';
import { Analysis } from '../../types';
import { CommunityPageProps } from './types';
import { ANALYSES_PER_PAGE } from './consts';
import { getThumbnailUrl, getChartUrl, getMapUrl, getAnimationUrl } from './functions';
import AnalysisThumbnail, { authorLabel, shortId } from '../AnalysisThumbnail';
import AnalysisDetails from '../AnalysisDetails';
import ConversationTimeline, { buildTimeline, ConversationGroup } from '../ConversationTimeline';
import Permalink from '../Permalink';
import { PAGE_PATHS, analysisPath, conversationPath, parseViewerTab, ViewerTab } from '../../App';
import './styles.scss';

/** Group one page of shared analyses by conversation. Conversations with
    2+ shared analyses become a single "conversation" card (like My Analyses);
    everything else stays an individual analysis card. */
const groupByConversation = (items: Analysis[]): { groups: ConversationGroup[]; singles: Analysis[] } => {
  const byConv: Record<string, Analysis[]> = {};
  const singles: Analysis[] = [];
  items.forEach(a => {
    if (a.conversation_id) (byConv[a.conversation_id] ||= []).push(a);
    else singles.push(a);
  });
  const groups: ConversationGroup[] = [];
  Object.entries(byConv).forEach(([cid, list]) => {
    if (list.length < 2) { singles.push(...list); return; }
    const sorted = [...list].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
    groups.push({
      conversation_id: cid,
      analyses: sorted,
      first_query: sorted[0].query,
      location: sorted[0].location,
      created_at: sorted[0].created_at,
      count: sorted.length,
      user_name: authorLabel(sorted[0]),
    });
  });
  // Keep the server's "most recently shared first" ordering for the mixed grid.
  const latest = (xs: Analysis[]) => Math.max(...xs.map(a => new Date(a.shared_at || a.created_at).getTime()));
  groups.sort((a, b) => latest(b.analyses) - latest(a.analyses));
  singles.sort((a, b) => latest([b]) - latest([a]));
  return { groups, singles };
};

const CommunityPage: React.FC<CommunityPageProps> = ({ setIsFullPageView, user }) => {
  const navigate = useNavigate();
  const { sessionId: routeSessionId, tab: routeTab, conversationId: routeConversationId } =
    useParams<{ sessionId?: string; tab?: string; conversationId?: string }>();
  // Tab is part of the URL: /community/<id> (map) or /community/<id>/charts
  const activeTab: ViewerTab = parseViewerTab(routeTab);
  const showChart = activeTab === 'charts';
  const showDetails = activeTab === 'details';
  const [analyses, setAnalyses] = useState<Analysis[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedAnalysis, setSelectedAnalysis] = useState<Analysis | null>(null);
  const [selectedMissing, setSelectedMissing] = useState(false);
  const [selectedConversation, setSelectedConversation] = useState<ConversationGroup | null>(null);
  const [conversationMissing, setConversationMissing] = useState(false);
  // Timeline to restore when the visitor presses Back from an analysis opened inside it.
  const returnToConversation = useRef<ConversationGroup | null>(null);
  const [userLikes, setUserLikes] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [viewerKey, setViewerKey] = useState(0);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  useEffect(() => {
    loadCommunityAnalyses();
  }, [user, currentPage]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setIsFullPageView(!!selectedAnalysis || !!selectedConversation);
  }, [selectedAnalysis, selectedConversation, setIsFullPageView]);

  // /community/c/<conversation_id> opens the shared conversation's timeline
  // (messages + every shared analysis), exactly like the owner's My Analyses view.
  useEffect(() => {
    let cancelled = false;
    if (!routeConversationId) {
      setSelectedConversation(null);
      setConversationMissing(false);
      return;
    }
    const cached = returnToConversation.current;
    if (cached?.conversation_id === routeConversationId) {
      returnToConversation.current = null;
      setSelectedConversation(cached);
      return;
    }
    if (selectedConversation?.conversation_id === routeConversationId) return;
    fetchCommunityConversation(routeConversationId, user?.id)
      .then(res => {
        if (cancelled) return;
        setSelectedConversation({
          conversation_id: res.conversation_id,
          analyses: res.analyses,
          first_query: res.first_query,
          location: res.location || res.analyses[0]?.location || '',
          created_at: res.created_at || res.analyses[0]?.created_at,
          count: res.count,
          user_name: res.user_name || authorLabel(res.analyses[0]),
          timeline: buildTimeline(res.messages, res.analyses),
        });
        setConversationMissing(false);
      })
      .catch(() => { if (!cancelled) { setSelectedConversation(null); setConversationMissing(true); } });
    return () => { cancelled = true; };
  }, [routeConversationId, user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // URL is the source of truth: /community/<session_id> opens that card.
  // Reuse the row from the loaded page when we have it, otherwise fetch it
  // (deep link / page reload / card on another page of the list).
  useEffect(() => {
    let cancelled = false;
    if (!routeSessionId) {
      setSelectedAnalysis(null);
      setSelectedMissing(false);
      return;
    }
    const local = analyses.find(a => a.session_id === routeSessionId);
    if (local) {
      setSelectedAnalysis(local);
      setSelectedMissing(false);
      return;
    }
    fetchAnalysis(routeSessionId, user?.id)
      .then(a => { if (!cancelled) { setSelectedAnalysis(a); setSelectedMissing(false); } })
      .catch(() => { if (!cancelled) { setSelectedAnalysis(null); setSelectedMissing(true); } });
    return () => { cancelled = true; };
  }, [routeSessionId, analyses, user?.id]);

  const openAnalysis = (item: Analysis, tab: ViewerTab = 'map', fromConversation: ConversationGroup | null = null) => {
    returnToConversation.current = fromConversation;
    if (fromConversation) setSelectedConversation(null);
    setViewerKey(prev => prev + 1);
    navigate(analysisPath(item.session_id, 'community', tab));
  };
  const setTab = (tab: ViewerTab) => {
    if (!selectedAnalysis) return;
    setViewerKey(prev => prev + 1);
    navigate(analysisPath(selectedAnalysis.session_id, 'community', tab), { replace: true });
  };
  const closeAnalysis = () => {
    const back = returnToConversation.current;
    navigate(back ? conversationPath(back.conversation_id, 'community') : PAGE_PATHS.community);
  };
  const openConversation = (group: ConversationGroup) => navigate(conversationPath(group.conversation_id, 'community'));
  const closeConversation = () => { returnToConversation.current = null; navigate(PAGE_PATHS.community); };

  const copyToClipboard = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      showToast('Link copied');
    } catch {
      showToast(url, 'error');
    }
  };
  const copyLink = (item: Analysis, tab?: ViewerTab) =>
    copyToClipboard(`${window.location.origin}${analysisPath(item.session_id, 'community', tab)}`);
  const copyConversationLink = (group: ConversationGroup) =>
    copyToClipboard(`${window.location.origin}${conversationPath(group.conversation_id, 'community')}`);

  const loadCommunityAnalyses = async () => {
    try {
      setLoading(true);

      const pageData = await fetchCommunity(currentPage, ANALYSES_PER_PAGE, user?.id);
      setTotalCount(pageData.total || 0);
      setAnalyses(pageData.items || []);
      setUserLikes(new Set(pageData.liked || []));
    } catch (err) {
      console.error('Failed to load community analyses:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLike = async (sessionId: string, _currentLikes: number) => {
    if (!user) {
      showToast('Please log in to like analyses', 'error');
      return;
    }

    try {
      const result = await toggleLike(sessionId, user.id);

      setUserLikes(prev => {
        const newSet = new Set(prev);
        if (result.liked) {
          newSet.add(sessionId);
        } else {
          newSet.delete(sessionId);
        }
        return newSet;
      });

      // Update local state
      setAnalyses(prev => prev.map(analysis =>
        analysis.session_id === sessionId
          ? { ...analysis, likes: result.likes, liked: result.liked }
          : analysis
      ));
      setSelectedAnalysis(prev =>
        prev && prev.session_id === sessionId ? { ...prev, likes: result.likes, liked: result.liked } : prev
      );
      setSelectedConversation(prev =>
        prev
          ? { ...prev, analyses: prev.analyses.map(a => a.session_id === sessionId ? { ...a, likes: result.likes, liked: result.liked } : a) }
          : prev
      );
    } catch (err) {
      console.error('Failed to toggle like:', err);
      showToast('Failed to update like', 'error');
    }
  };

  if (routeSessionId && selectedMissing) {
    return (
      <div className="page-container">
        <div className="empty-state">
          <div className="empty-icon">🔍</div>
          <h3>Analysis not found</h3>
          <p>This analysis doesn't exist, isn't shared, or its ID is wrong.</p>
          <p className="analysis-card-id" title={routeSessionId}>{routeSessionId}</p>
          <button onClick={closeAnalysis} className="btn btn-secondary">← Back to Community</button>
        </div>
      </div>
    );
  }

  if (routeConversationId && conversationMissing) {
    return (
      <div className="page-container">
        <div className="empty-state">
          <div className="empty-icon">🔍</div>
          <h3>Conversation not found</h3>
          <p>This conversation doesn't exist, hasn't been shared, or its ID is wrong.</p>
          <p className="analysis-card-id" title={routeConversationId}>{routeConversationId}</p>
          <button onClick={closeConversation} className="btn btn-secondary">← Back to Community</button>
        </div>
      </div>
    );
  }

  if (loading || (routeSessionId && !selectedAnalysis) || (routeConversationId && !selectedConversation)) {
    return (
      <div className="page-container">
        <div className="loading-text">Loading community research...</div>
      </div>
    );
  }

  // Shared conversation timeline — same component the owner sees under My Analyses.
  if (selectedConversation) {
    const totalLikes = selectedConversation.analyses.reduce((n, a) => n + (a.likes || 0), 0);
    return (
      <ConversationTimeline
        conversation={selectedConversation}
        scope="community"
        userLabel={selectedConversation.user_name || 'Author'}
        onBack={closeConversation}
        backLabel="← Back to Community"
        onOpenAnalysis={(a, tab) => openAnalysis(a, tab, selectedConversation)}
        subtitle={
          <>
            By {selectedConversation.user_name || 'Anonymous'}
            {selectedConversation.location && <> • 📍 {selectedConversation.location}</>}
            {' • '}{selectedConversation.count} {selectedConversation.count === 1 ? 'analysis' : 'analyses'}
            {' • '}{new Date(selectedConversation.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
            {totalLikes > 0 && <> • 👍 {totalLikes}</>}
          </>
        }
        permalink={<Permalink path={conversationPath(selectedConversation.conversation_id, 'community')} isPublic />}
        headerActions={
          <button onClick={() => copyConversationLink(selectedConversation)} className="btn btn-secondary" title="Copy a shareable link to this conversation">
            🔗 Copy link
          </button>
        }
      />
    );
  }

  if (selectedAnalysis) {
    const isAnimation = selectedAnalysis.analysis_type === 'video_animation';
    const liked = userLikes.has(selectedAnalysis.session_id) || selectedAnalysis.liked === true;
    const backTo = returnToConversation.current;
    return (
      <div className="page-container-full">
        <div className="viewer-header">
          <button onClick={closeAnalysis} className="btn btn-secondary">
            {backTo ? '← Back to Conversation' : '← Back to List'}
          </button>
          <div className="viewer-header-info">
            <h2>{selectedAnalysis.query}</h2>
            <div className="viewer-conversation-info">
              By {authorLabel(selectedAnalysis)}
              {' · '}<span className="analysis-card-id" title={selectedAnalysis.session_id}>{shortId(selectedAnalysis)}</span>
              {selectedAnalysis.conversation_id && !backTo && (
                <>
                  {' · '}
                  <a
                    href={conversationPath(selectedAnalysis.conversation_id, 'community')}
                    onClick={(e) => { e.preventDefault(); navigate(conversationPath(selectedAnalysis.conversation_id!, 'community')); }}
                    className="viewer-conversation-link"
                  >
                    💬 View full conversation
                  </a>
                </>
              )}
              {selectedAnalysis.location && <> · 📍 {selectedAnalysis.location}</>}
              {(selectedAnalysis.start_year || selectedAnalysis.end_year) && (
                <> · {selectedAnalysis.start_year || '—'} → {selectedAnalysis.end_year || '—'}</>
              )}
              {' · '}{new Date(selectedAnalysis.shared_at || selectedAnalysis.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
            </div>
          </div>
          <div className="viewer-tabs">
            <button onClick={() => setTab('map')} className={`viewer-tab ${activeTab === 'map' ? 'active' : ''}`}>
              {isAnimation ? 'Animation' : 'Map'}
            </button>
            <button onClick={() => setTab('charts')} className={`viewer-tab ${showChart ? 'active' : ''}`}>
              {isAnimation ? 'Preview' : 'Charts'}
            </button>
            <button onClick={() => setTab('details')} className={`viewer-tab ${showDetails ? 'active' : ''}`}>
              Details
            </button>
          </div>
          <button
            onClick={() => handleLike(selectedAnalysis.session_id, selectedAnalysis.likes || 0)}
            className={`btn btn-secondary like-btn ${liked ? 'liked' : ''}`}
            title={liked ? 'Click to unlike' : 'Like this analysis'}
          >
            👍 {selectedAnalysis.likes || 0}
          </button>
          <button onClick={() => copyLink(selectedAnalysis, activeTab)} className="btn btn-secondary" title="Copy a shareable link to this view">
            🔗 Copy link
          </button>
        </div>
        <Permalink path={analysisPath(selectedAnalysis.session_id, 'community', activeTab)} isPublic />
        <div className="full-viewer">
          {showDetails ? (
            <AnalysisDetails analysis={selectedAnalysis} />
          ) : selectedAnalysis.artifacts_expired ? (
            <div className="viewer-expired">
              <div className="placeholder-icon">🗂️</div>
              <p>The result files for this analysis are no longer available.</p>
            </div>
          ) : isAnimation ? (
            // Timelapse analyses have no map.html; the "map" slot is the GIF
            // and the "chart" slot is the frame-grid preview.
            <img
              key={viewerKey}
              src={showChart ? getChartUrl(selectedAnalysis) : getAnimationUrl(selectedAnalysis)}
              alt={showChart ? 'Animation preview grid' : 'Satellite timelapse animation'}
              className="full-chart-img"
            />
          ) : !showChart ? (
            <iframe
              key={viewerKey}
              src={getMapUrl(selectedAnalysis)}
              className="full-iframe"
              title="Analysis Results"
            />
          ) : (
            <img
              key={viewerKey}
              src={getChartUrl(selectedAnalysis)}
              alt="Analysis Charts"
              className="full-chart-img"
            />
          )}
        </div>
      </div>
    );
  }

  const { groups: conversationGroups, singles } = groupByConversation(analyses);

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Community Research</h1>
        <p>Explore analyses shared by the community</p>
      </div>

      {analyses.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">🌍</div>
          <h3>No shared analyses yet</h3>
          <p>Be the first to share your analysis with the community!</p>
        </div>
      ) : (
        <div className="analysis-grid">
          {conversationGroups.map((group) => {
            const groupLikes = group.analyses.reduce((n, a) => n + (a.likes || 0), 0);
            return (
              <div
                key={`conv-${group.conversation_id}`}
                id={`conversation-${group.conversation_id}`}
                data-conversation-id={group.conversation_id}
                className="analysis-card conversation-card"
                onClick={() => openConversation(group)}
                role="link"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter') openConversation(group); }}
              >
                <div className="card-badge conversation-badge">
                  💬 {group.count} {group.count === 1 ? 'Analysis' : 'Analyses'}
                </div>
                <AnalysisThumbnail item={group.analyses[0]} src={getThumbnailUrl(group.analyses[0])} alt="conversation" fallbackIcon="💬" />
                <div className="analysis-card-content">
                  <h3 className="conversation-card-title">{group.first_query.length > 100 ? group.first_query.substring(0, 100) + '...' : group.first_query}</h3>
                  <span className={`analysis-type-tag analysis-type-${group.analyses[0]?.analysis_type || 'unknown'}`}>
                    {(group.analyses[0]?.analysis_type || 'analysis').replace(/_/g, ' ')}
                  </span>
                  <p className="analysis-location">📍 {group.location}</p>
                  <p className="analysis-author">By: {group.user_name}</p>
                  <div className="analysis-meta-row">
                    <p className="analysis-date">
                      {new Date(group.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                    </p>
                    <p className="analysis-card-id" title={`Conversation ID: ${group.conversation_id}`}>{shortId(group.analyses[0])}</p>
                  </div>
                  <div className="card-actions">
                    <a
                      href={conversationPath(group.conversation_id, 'community')}
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); openConversation(group); }}
                      className="btn btn-primary btn-small flex-auto"
                    >
                      Timeline
                    </a>
                    <button
                      onClick={(e) => { e.stopPropagation(); copyConversationLink(group); }}
                      className="btn btn-secondary btn-small"
                      title="Copy link to this conversation"
                    >
                      🔗
                    </button>
                    <span className="btn btn-secondary btn-small like-btn like-btn-static" title="Total likes across this conversation">
                      👍 {groupLikes}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}

          {singles.map((item) => (
            <div
              key={item.session_id || item.id}
              id={`analysis-${item.session_id || item.id}`}
              data-session-id={item.session_id}
              className="analysis-card"
              onClick={() => openAnalysis(item)}
              role="link"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter') openAnalysis(item); }}
            >
              <div className="card-badge community-badge">Community</div>
              {item.conversation_id && <div className="card-badge conversation-badge-secondary">💬 Conversation</div>}

              <AnalysisThumbnail item={item} src={getThumbnailUrl(item)} />

              <div className="analysis-card-content">
                <h3>{item.analysis_type}</h3>
                <p className="analysis-query">{item.query}</p>
                <p className="analysis-location">📍 {item.location}</p>
                <p className="analysis-author">
                  By: {authorLabel(item)}
                </p>
                <div className="analysis-meta-row">
                  <p className="analysis-date">
                    {new Date(item.shared_at || item.created_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    })}
                  </p>
                  <p className="analysis-card-id" title={`Analysis ID: ${item.session_id}`}>{shortId(item)}</p>
                </div>

                <div className="card-actions">
                  <a
                    href={analysisPath(item.session_id, 'community')}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      openAnalysis(item);
                    }}
                    className="btn btn-primary btn-small flex-auto"
                  >
                    Results
                  </a>
                  <button
                    onClick={(e) => { e.stopPropagation(); copyLink(item); }}
                    className="btn btn-secondary btn-small"
                    title="Copy link to this analysis"
                  >
                    🔗
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleLike(item.session_id, item.likes || 0);
                    }}
                    className={`btn btn-secondary btn-small like-btn ${userLikes.has(item.session_id) ? 'liked' : ''}`}
                    title={userLikes.has(item.session_id) ? "Click to unlike" : "Like this analysis"}
                  >
                    👍 {item.likes || 0}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && analyses.length > 0 && totalCount > ANALYSES_PER_PAGE && (
        <div className="pagination-controls">
          <button
            onClick={() => { setCurrentPage(prev => Math.max(prev - 1, 1)); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            disabled={currentPage === 1}
            className="btn btn-secondary btn-small"
          >
            ← Previous
          </button>
          <div className="pagination-pages">
            {Array.from({ length: Math.ceil(totalCount / ANALYSES_PER_PAGE) }, (_, i) => i + 1).map(pageNum => (
              <button
                key={pageNum}
                onClick={() => { setCurrentPage(pageNum); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                className={`pagination-page-btn ${currentPage === pageNum ? 'active' : ''}`}
              >
                {pageNum}
              </button>
            ))}
          </div>
          <button
            onClick={() => { setCurrentPage(prev => Math.min(prev + 1, Math.ceil(totalCount / ANALYSES_PER_PAGE))); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            disabled={currentPage >= Math.ceil(totalCount / ANALYSES_PER_PAGE)}
            className="btn btn-secondary btn-small"
          >
            Next →
          </button>
        </div>
      )}

      {!loading && analyses.length > 0 && (
        <div className="analysis-count-text">
          Showing {((currentPage - 1) * ANALYSES_PER_PAGE) + 1}–{Math.min(currentPage * ANALYSES_PER_PAGE, totalCount)} of {totalCount} analyses
        </div>
      )}

      {toast && (
        <div className={`toast-notification toast-${toast.type}`}>
          <span>{toast.message}</span>
          <button className="toast-close" onClick={() => setToast(null)}>×</button>
        </div>
      )}
    </div>
  );
};

export default CommunityPage;
